// Shared shell state (palette, shortcuts dialog, sidebar) + guarded navigation + recent pages.
import { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { confirmNavigation, onTitleChange, getPageTitle, refreshStoreEvents } from '@/lib/hooks'
import { useSession } from '@/lib/session'
import { matchRoute, listPathFor } from './route-meta'

export type ShellKind = 'store' | 'platform' | 'full' | 'bare' | 'public' | 'raw'

export interface ShellApi {
  kind: ShellKind
  openPalette: (page?: 'root' | 'stores' | 'theme') => void
  openShortcuts: () => void
  toggleSidebar: () => void
  sidebarCollapsed: boolean
  openMobileNav: () => void
}
export const ShellContext = createContext<ShellApi | null>(null)
export const useShell = () => useContext(ShellContext)

/** navigate() that asks first when a form has unsaved changes. */
export function useGuardedNavigate() {
  const navigate = useNavigate()
  return useCallback(async (to: string) => {
    if (await confirmNavigation()) navigate(to)
  }, [navigate])
}

/** Switch the current store: guarded, keeps you in the same section (detail pages go to their list). */
export function useSwitchStore() {
  const { switchStore, storeId } = useSession()
  const navigate = useNavigate()
  const loc = useLocation()
  return useCallback(async (id: string) => {
    if (id === storeId) return
    if (!(await confirmNavigation())) return
    const target = matchRoute(loc.pathname)?.layout === 'store' || matchRoute(loc.pathname)?.layout === 'full' ? listPathFor(loc.pathname) : '/'
    switchStore(id)
    refreshStoreEvents()
    navigate(target, { replace: false })
  }, [switchStore, storeId, navigate, loc.pathname])
}

// ─── recent pages (per store, localStorage) ─────────────────────────────────────────────

export interface RecentPage { path: string; title: string; at: number }
const MAX_RECENTS = 8
const keyFor = (scope: string) => `cmdk-recent:${scope}`

export function readRecents(scope: string): RecentPage[] {
  try {
    const v = JSON.parse(localStorage.getItem(keyFor(scope)) ?? '[]') as unknown
    return Array.isArray(v) ? v.filter((r): r is RecentPage => !!r && typeof r.path === 'string' && typeof r.title === 'string' && r.path.startsWith('/') && !r.path.startsWith('//')).slice(0, MAX_RECENTS) : []
  } catch { return [] }
}
function writeRecent(scope: string, entry: RecentPage) {
  try {
    const list = readRecents(scope).filter(r => r.path.split('?')[0] !== entry.path.split('?')[0])
    localStorage.setItem(keyFor(scope), JSON.stringify([entry, ...list].slice(0, MAX_RECENTS)))
  } catch { /* storage blocked */ }
}

/** Records visited pages (with their final page title) for ⌘K "Recent". */
export function useRecentTracker(scope: string | null) {
  const loc = useLocation()
  const scopeRef = useRef(scope)
  scopeRef.current = scope
  useEffect(() => {
    if (!scope) return
    const route = matchRoute(loc.pathname)
    if (!route || route.layout === 'public' || route.layout === 'raw') return
    const path = loc.pathname + loc.search
    const record = (title: string | null) => { if (scopeRef.current === scope) writeRecent(scope, { path, title: title || route.title, at: Date.now() }) }
    // Wait a beat so pages that set their own title (usePageTitle) are captured.
    const t = setTimeout(() => record(getPageTitle()), 800)
    const off = onTitleChange((title, p) => { if (p === loc.pathname && title) record(title) })
    return () => { clearTimeout(t); off() }
  }, [loc.pathname, loc.search, scope])
}
